/**
 * Helpers for applying GET /v1/config fields in the panel.
 */

import type { WidgetConfig, WidgetFeatures } from '../types/api';

/** Feature flags from config — defaults match MVP (text-only). */
export const DEFAULT_FEATURES: WidgetFeatures = {
  file_upload: false,
  voice: false,
  pre_chat_form: false,
};

export function resolveFeatures(
  features: WidgetFeatures | undefined | null,
): WidgetFeatures {
  return {
    file_upload: Boolean(features?.file_upload),
    voice: Boolean(features?.voice),
    pre_chat_form: Boolean(features?.pre_chat_form),
  };
}

/** Non-empty question strings from GET /v1/config.popular_questions. */
export function resolvePopularQuestions(
  questions: WidgetConfig['popular_questions'] | undefined | null,
): string[] {
  if (!questions?.length) return [];
  return questions
    .map((item) => (item?.question || '').trim())
    .filter(Boolean);
}

/**
 * Whether the host should show the chat bubble.
 * `widget_status` is the first indicator — AND more flags here as they land.
 */
export function isLauncherEnabled(
  config: Pick<WidgetConfig, 'widget_status'>,
): boolean {
  return config.widget_status !== false;
}

/** True when Gateway requires HMAC identity binding. */
export function isIdentityVerificationEnabled(
  value: string | undefined | null,
): boolean {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized !== '' && normalized !== 'disabled' && normalized !== 'off';
}

/**
 * Best-effort embedding host origin (customer site).
 * Prefer ancestor / referrer over the panel iframe origin — used for
 * soft allowlist checks and the `X-Embed-Origin` header for Gateway.
 */
export function resolveHostOrigin(): string {
  if (typeof window === 'undefined') return '';

  try {
    const ancestors = (
      window as Window & { location: Location & { ancestorOrigins?: DOMStringList } }
    ).location.ancestorOrigins;
    if (ancestors && ancestors.length > 0 && ancestors[0]) {
      return new URL(ancestors[0]).origin;
    }
  } catch {
    // ignore
  }

  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // ignore
  }

  return window.location.origin;
}

/**
 * Header telling Gateway which site embeds the widget.
 * Browser `Origin` is the panel host; this is the customer site.
 * Empty when origin cannot be resolved (omit header).
 */
export function embedOriginHeaders(
  origin: string = resolveHostOrigin(),
): Record<string, string> {
  if (!origin) return {};
  return { 'X-Embed-Origin': origin };
}

/**
 * Soft client allowlist check. Empty allowlist → skip (server still enforces).
 * Returns null when allowed, or a human-readable reason when blocked.
 */
export function allowlistBlockReason(
  config: Pick<WidgetConfig, 'allowlist'>,
  origin: string = resolveHostOrigin(),
): string | null {
  const origins = config.allowlist?.origins ?? [];
  if (origins.length === 0) return null;
  if (!origin) return null;

  const normalized = origin.replace(/\/$/, '').toLowerCase();
  const allowed = origins.some(
    (item) => item.replace(/\/$/, '').toLowerCase() === normalized,
  );

  if (allowed) return null;
  return `Origin '${origin}' is not allowlisted for this avatar.`;
}
